package ro.horatiu_udrea.resursedeblocat

import androidx.compose.animation.AnimatedVisibility
import androidx.compose.foundation.BorderStroke
import androidx.compose.foundation.background
import androidx.compose.foundation.layout.*
import androidx.compose.foundation.shape.CircleShape
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.automirrored.filled.KeyboardArrowLeft
import androidx.compose.material.icons.filled.ExpandLess
import androidx.compose.material.icons.filled.ExpandMore
import androidx.compose.material3.*
import androidx.compose.runtime.*
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.layout.ContentScale
import androidx.compose.ui.text.font.FontStyle
import androidx.compose.ui.unit.dp
import coil3.compose.AsyncImage
import org.jetbrains.compose.resources.stringResource
import resursedeblocat.composeapp.generated.resources.*

@OptIn(ExperimentalMaterial3Api::class)
@Composable
fun AdminScreen(
    viewModel: AdminViewModel,
    onLogout: () -> Unit
) {
    LaunchedEffect(Unit) {
        viewModel.loadUsers()
    }

    DisposableEffect(Unit) {
        onDispose {
            viewModel.onDispose()
        }
    }

    val selectedUser = viewModel.selectedUser

    if (selectedUser == null) {
        UserListScreen(
            users = viewModel.users,
            lastTrackings = viewModel.userLastTrackings,
            isLoading = viewModel.isLoadingUsers,
            onUserClick = { viewModel.selectUser(it) },
            onLogout = onLogout
        )
    } else {
        UserDetailScreen(
            user = selectedUser,
            resources = viewModel.userResources,
            trackings = viewModel.userTrackings,
            isLoading = viewModel.isLoadingUserData,
            onBack = { viewModel.selectUser(null) }
        )
    }
}

@Composable
fun UserListScreen(
    users: List<User>,
    lastTrackings: Map<String, MonthlyTracking?>,
    isLoading: Boolean,
    onUserClick: (User) -> Unit,
    onLogout: () -> Unit
) {
    BaseScreenLayout(
        footer = {
            Spacer(modifier = Modifier.height(16.dp))
            TextButton(
                onClick = onLogout,
                modifier = Modifier.padding(bottom = 8.dp)
            ) {
                Text(
                    text = stringResource(Res.string.logout),
                    style = MaterialTheme.typography.labelLarge,
                    color = MaterialTheme.colorScheme.error
                )
            }
        }
    ) {
        BoxWithConstraints(modifier = Modifier.fillMaxWidth().padding(bottom = 16.dp)) {
            val isWide = maxWidth > 500.dp
            Column(
                modifier = Modifier.fillMaxWidth().padding(horizontal = 12.dp),
                horizontalAlignment = if (isWide) Alignment.Start else Alignment.CenterHorizontally
            ) {
                Text(
                    text = stringResource(Res.string.tab_admin),
                    style = MaterialTheme.typography.headlineMedium,
                    color = MaterialTheme.colorScheme.secondary
                )
            }
        }

        if (isLoading) {
            Box(Modifier.fillMaxWidth().padding(32.dp), contentAlignment = Alignment.Center) {
                CircularProgressIndicator()
            }
        } else {
            Column(
                modifier = Modifier.fillMaxWidth(),
                verticalArrangement = Arrangement.spacedBy(8.dp)
            ) {
                users.forEach { user ->
                    UserCard(
                        user = user,
                        lastTracking = lastTrackings[user.id],
                        onClick = { onUserClick(user) }
                    )
                }
            }
        }
    }
}

@Composable
fun UserCard(user: User, lastTracking: MonthlyTracking?, onClick: () -> Unit) {
    val totalExpected = 10 // 9 steps + 1 dual field/completion buffer
    val answersCount = lastTracking?.answers?.size ?: 0
    val displayProgress = if (lastTracking?.completed == true) "$totalExpected/$totalExpected" else "$answersCount/$totalExpected"
    
    Card(
        onClick = onClick,
        modifier = Modifier.fillMaxWidth(),
        colors = CardDefaults.cardColors(containerColor = Color.White),
        border = BorderStroke(1.dp, MaterialTheme.colorScheme.outlineVariant.copy(alpha = 0.5f))
    ) {
        Row(
            modifier = Modifier.padding(16.dp),
            verticalAlignment = Alignment.CenterVertically,
            horizontalArrangement = Arrangement.spacedBy(16.dp)
        ) {
            AsyncImage(
                model = user.photoUrl ?: "https://ui-avatars.com/api/?name=${user.name ?: "User"}",
                contentDescription = null,
                modifier = Modifier.size(48.dp).clip(CircleShape),
                contentScale = ContentScale.Crop
            )
            Column(modifier = Modifier.weight(1f)) {
                Text(user.name ?: stringResource(Res.string.admin_unknown_user), style = MaterialTheme.typography.titleMedium)
                Text(user.email ?: stringResource(Res.string.admin_no_email), style = MaterialTheme.typography.bodySmall)
            }
            
            if (answersCount > 0 || lastTracking?.completed == true) {
                Text(
                    text = if (lastTracking?.completed == true) "Completat" else displayProgress,
                    style = MaterialTheme.typography.labelSmall,
                    color = ThemePrimary,
                    modifier = Modifier
                        .background(ThemePrimary.copy(alpha = 0.1f), RoundedCornerShape(4.dp))
                        .padding(horizontal = 6.dp, vertical = 2.dp)
                )
            }
        }
    }
}

@Composable
fun UserDetailScreen(
    user: User,
    resources: List<ResourceDetails>,
    trackings: List<MonthlyTracking>,
    isLoading: Boolean,
    onBack: () -> Unit
) {
    var selectedResource by remember { mutableStateOf<ResourceDetails?>(null) }

    if (selectedResource != null) {
        ResourceDetailPage(
            details = selectedResource!!,
            onBack = { selectedResource = null },
            showOptions = false
        )
    } else {
        BaseScreenLayout {
            BoxWithConstraints(modifier = Modifier.fillMaxWidth().padding(bottom = 16.dp)) {
                val isWide = maxWidth > 500.dp
                Row(
                    modifier = Modifier.fillMaxWidth(),
                    verticalAlignment = Alignment.CenterVertically,
                    horizontalArrangement = Arrangement.spacedBy(12.dp)
                ) {
                    IconButton(onClick = onBack) {
                        Icon(Icons.AutoMirrored.Filled.KeyboardArrowLeft, contentDescription = "Back")
                    }
                    
                    AsyncImage(
                        model = user.photoUrl ?: "https://ui-avatars.com/api/?name=${user.name ?: "User"}",
                        contentDescription = null,
                        modifier = Modifier.size(if (isWide) 48.dp else 40.dp).clip(CircleShape)
                    )
                    
                    Column {
                        Text(
                            user.name ?: stringResource(Res.string.admin_unknown_user),
                            style = if (isWide) MaterialTheme.typography.headlineSmall else MaterialTheme.typography.titleLarge,
                            color = MaterialTheme.colorScheme.secondary
                        )
                        Text(
                            user.email ?: stringResource(Res.string.admin_no_email),
                            style = MaterialTheme.typography.bodySmall,
                            color = Color.Gray
                        )
                    }
                }
            }

            if (isLoading) {
                Box(Modifier.fillMaxWidth().padding(32.dp), contentAlignment = Alignment.Center) {
                    CircularProgressIndicator()
                }
            } else {
                Column(
                    modifier = Modifier.fillMaxWidth(),
                    verticalArrangement = Arrangement.spacedBy(16.dp)
                ) {
                    Text(
                        stringResource(Res.string.admin_checkins_count, trackings.size),
                        style = MaterialTheme.typography.titleLarge,
                        color = ThemePrimary,
                        modifier = Modifier.padding(top = 8.dp)
                    )
                    
                    if (trackings.isEmpty()) {
                        Text(stringResource(Res.string.admin_no_checkins), style = MaterialTheme.typography.bodyMedium)
                    } else {
                        trackings.forEach { tracking ->
                            AdminTrackingCard(tracking = tracking)
                        }
                    }

                    HorizontalDivider(modifier = Modifier.padding(vertical = 8.dp), color = MaterialTheme.colorScheme.outlineVariant)

                    Text(
                        stringResource(Res.string.admin_resources_count, resources.size),
                        style = MaterialTheme.typography.titleLarge,
                        color = ThemePrimary
                    )
                    
                    if (resources.isEmpty()) {
                        Text(stringResource(Res.string.admin_no_resources), style = MaterialTheme.typography.bodyMedium)
                    } else {
                        resources.forEach { resource ->
                            ResourceCard(resource = resource, onClick = { selectedResource = resource })
                        }
                    }
                }
            }
        }
    }
}

@Composable
fun AdminTrackingCard(tracking: MonthlyTracking) {
    var expanded by remember { mutableStateOf(false) }
    val monthName = stringResource(when(tracking.month) {
        1 -> Res.string.month_1
        2 -> Res.string.month_2
        3 -> Res.string.month_3
        4 -> Res.string.month_4
        5 -> Res.string.month_5
        6 -> Res.string.month_6
        7 -> Res.string.month_7
        8 -> Res.string.month_8
        9 -> Res.string.month_9
        10 -> Res.string.month_10
        11 -> Res.string.month_11
        12 -> Res.string.month_12
        else -> Res.string.month_1
    })

    Card(
        modifier = Modifier.fillMaxWidth(),
        onClick = { expanded = !expanded },
        colors = CardDefaults.cardColors(containerColor = Color.White),
        border = BorderStroke(1.dp, MaterialTheme.colorScheme.outlineVariant.copy(alpha = 0.5f))
    ) {
        Column(modifier = Modifier.padding(16.dp)) {
            Row(
                modifier = Modifier.fillMaxWidth(),
                horizontalArrangement = Arrangement.SpaceBetween,
                verticalAlignment = Alignment.CenterVertically
            ) {
                Text("$monthName ${tracking.year}", style = MaterialTheme.typography.titleMedium)
                Icon(if (expanded) Icons.Default.ExpandLess else Icons.Default.ExpandMore, contentDescription = null)
            }
            
            AnimatedVisibility(visible = expanded) {
                val orderedKeys = listOf(
                    theoryResourcesKey,
                    practicalActionsKey,
                    workHoursKey,
                    workEnergyKey,
                    environmentEnergyKey,
                    weeklyIncomeKey,
                    wellBeingKey,
                    notWellBeingKey,
                    participatedMinigroupKey,
                    interactionMinigroupKey,
                    joinMinigroupKey,
                    deblocatMomentKey,
                    anythingElseKey
                )
                Column(modifier = Modifier.padding(top = 16.dp), verticalArrangement = Arrangement.spacedBy(12.dp)) {
                    orderedKeys.forEach { key ->
                        TrackingAnswerItem(questionKey = key, value = tracking.answers[key])
                    }
                }
            }
        }
    }
}

@Composable
fun TrackingAnswerItem(questionKey: String, value: Any?) {
    val title = when (questionKey) {
        theoryResourcesKey -> stringResource(Res.string.tracking_theory_title)
        practicalActionsKey -> stringResource(Res.string.tracking_practical_title)
        workHoursKey -> stringResource(Res.string.tracking_work_hours_title)
        workEnergyKey -> stringResource(Res.string.tracking_energy_work_label)
        environmentEnergyKey -> stringResource(Res.string.tracking_energy_env_label)
        wellBeingKey -> stringResource(Res.string.tracking_well_being_title)
        weeklyIncomeKey -> stringResource(Res.string.tracking_weekly_income_title)
        notWellBeingKey -> stringResource(Res.string.tracking_not_well_being_title)
        participatedMinigroupKey -> stringResource(Res.string.tracking_participated_minigroup_title)
        interactionMinigroupKey -> stringResource(Res.string.tracking_interaction_minigroup_title)
        joinMinigroupKey -> stringResource(Res.string.tracking_join_minigroup_title, "...")
        deblocatMomentKey -> stringResource(Res.string.tracking_deblocat_title)
        anythingElseKey -> stringResource(Res.string.tracking_anything_else_title)
        else -> questionKey
    }

    Column(modifier = Modifier.padding(bottom = 4.dp)) {
        Text(title, style = MaterialTheme.typography.labelLarge, color = ThemePrimary)
        Spacer(modifier = Modifier.height(4.dp))
        
        if (value == null) {
            Text(
                text = stringResource(Res.string.admin_missing_answer),
                style = MaterialTheme.typography.bodyMedium,
                color = MaterialTheme.colorScheme.error,
                fontStyle = FontStyle.Italic
            )
        } else {
            when (value) {
                is List<*> -> {
                    Column(modifier = Modifier.padding(start = 8.dp)) {
                        val items = value.filterIsInstance<String>().filter { it.isNotBlank() }
                        if (items.isEmpty()) {
                            Text(stringResource(Res.string.admin_missing_answer), style = MaterialTheme.typography.bodyMedium, color = MaterialTheme.colorScheme.error, fontStyle = FontStyle.Italic)
                        } else {
                            items.forEach { item ->
                                Text("• $item", style = MaterialTheme.typography.bodyMedium, color = Color.Gray)
                            }
                        }
                    }
                }
                is Int, is Float -> {
                    val floatValue = value.toString().toFloatOrNull() ?: 0f
                    if (questionKey == workHoursKey) {
                        Text("${floatValue.toInt()} ore", style = MaterialTheme.typography.bodyMedium, color = Color.Gray)
                    } else if (questionKey == weeklyIncomeKey) {
                        Text("${floatValue.toInt()} lei", style = MaterialTheme.typography.bodyMedium, color = Color.Gray)
                    } else {
                        // Slider for energy (1-10)
                        LinearProgressIndicator(
                            progress = { floatValue / 10f },
                            modifier = Modifier.fillMaxWidth().height(8.dp).clip(RoundedCornerShape(4.dp)),
                            color = ThemePrimary,
                            trackColor = ThemePrimary.copy(alpha = 0.1f)
                        )
                        Text("${floatValue.toInt()}/10", style = MaterialTheme.typography.bodySmall, color = Color.Gray)
                    }
                }
                is Boolean -> {
                    Text(
                        text = stringResource(if (value) Res.string.tracking_yes else Res.string.tracking_no),
                        style = MaterialTheme.typography.bodyMedium,
                        color = Color.Gray
                    )
                }
                else -> {
                    val text = value.toString()
                    if (text.isBlank()) {
                        Text(stringResource(Res.string.admin_missing_answer), style = MaterialTheme.typography.bodyMedium, color = MaterialTheme.colorScheme.error, fontStyle = FontStyle.Italic)
                    } else {
                        Text(text, style = MaterialTheme.typography.bodyMedium, color = Color.Gray)
                    }
                }
            }
        }
    }
}
